import { getUnmappedCustomFieldsCount } from '../../../store/salesChannels';
import resolveSalesChannelId from '../../../store/products/helpers/resolveSalesChannelId';
import { LocalStorage } from '../../../shared/utils';
import {
  PUBLISH_UNMAPPED_FIELDS_SKIP_KEY,
  showPublishUnmappedFieldsDialog,
} from './showPublishUnmappedFieldsDialog';

function getChannelLabel(channelId, salesChannels = []) {
  const channel = salesChannels.find(
    (salesChannel) => salesChannel.type === channelId
      || salesChannel.name === channelId
      || String(salesChannel._id) === channelId,
  );

  return channel?.name || channelId;
}

function buildChannelEntries(channelIds, salesChannels) {
  return channelIds.map((channelId) => ({
    channelId,
    salesChannelId: resolveSalesChannelId(channelId, salesChannels),
  }));
}

function getUniqueSalesChannelIds(channelEntries) {
  return [...new Set(channelEntries.map(({ salesChannelId }) => salesChannelId))];
}

function buildUnmappedChannelsSummary(channelEntries, countsBySalesChannelId, salesChannels) {
  const channelsWithUnmapped = [];
  let fullyMappedCount = 0;

  channelEntries.forEach(({ channelId, salesChannelId }) => {
    const unmappedFieldCount = countsBySalesChannelId[salesChannelId]?.unmappedCustomFieldCount ?? 0;

    if (unmappedFieldCount > 0) {
      channelsWithUnmapped.push({
        channelLabel: getChannelLabel(channelId, salesChannels),
        unmappedFieldCount,
        salesChannelId,
      });
      return;
    }

    fullyMappedCount += 1;
  });

  return {
    channelsWithUnmapped,
    fullyMappedCount,
  };
}

export default async function runPublishUnmappedFieldsGuard({
  channelIds,
  dispatch,
  salesChannels,
  onProceed,
  onAbort,
  onUnmappedDialogOpen,
}) {
  if (LocalStorage.get(PUBLISH_UNMAPPED_FIELDS_SKIP_KEY)) {
    onProceed();
    return;
  }

  if (!channelIds.length) {
    onProceed();
    return;
  }

  const channelEntries = buildChannelEntries(channelIds, salesChannels);
  const salesChannelIds = getUniqueSalesChannelIds(channelEntries);

  let countsBySalesChannelId = {};

  try {
    const response = await dispatch(getUnmappedCustomFieldsCount({ salesChannelIds })).unwrap();
    countsBySalesChannelId = response?.bySalesChannelId || {};
  } catch (error) {
    onProceed();
    return;
  }

  const { channelsWithUnmapped, fullyMappedCount } = buildUnmappedChannelsSummary(
    channelEntries,
    countsBySalesChannelId,
    salesChannels,
  );

  if (!channelsWithUnmapped.length) {
    onProceed();
    return;
  }

  try {
    onUnmappedDialogOpen?.();
    await showPublishUnmappedFieldsDialog({
      channelsWithUnmapped,
      fullyMappedCount,
      totalChannelCount: channelEntries.length,
    });
  } catch (error) {
    if (error?.message === 'cancel') {
      onAbort?.();
      return;
    }
    throw error;
  }

  onProceed();
}

export function validatePublishUnmappedFieldsGuard({
  channelIds,
  dispatch,
  salesChannels,
  onAbort,
  onUnmappedDialogOpen,
}) {
  return new Promise((resolve, reject) => {
    runPublishUnmappedFieldsGuard({
      channelIds,
      dispatch,
      salesChannels,
      onProceed: resolve,
      onAbort: () => {
        onAbort?.();
        reject(new Error('abort'));
      },
      onUnmappedDialogOpen,
    }).catch(reject);
  });
}

export { buildUnmappedChannelsSummary };
